// KnockoutComplete.js
// Knockout results / share screen — web-only design (intentionally diverges from
// the iOS complete screen and from DraftComplete).
//
// Goals:
//   • Strong, screenshot-worthy share screen that reveals NO roster answers
//     (no roster grid, and chemistry shows only the overall total — never the
//     specific team/city bonuses).
//   • Large centered score wheel + an enlarged chemistry total beneath it.
//   • A pool title header ("BASKETBALL KNOCKOUT #3").
//   • A live cut-line status — "CURRENTLY ADVANCING" / "ON THE BUBBLE" /
//     "BELOW THE CUT" — derived from the pool day's estimatedCutScore (written
//     every 10 min by refreshKnockoutCutLines). Advancing = score >= cut.
//   • A real SHARE button: native share sheet with a generated PNG on mobile,
//     PNG download on desktop, text copy as a final fallback. Mirrors the share
//     pattern used by DraftComplete.
//
// Two states: completed (team present) and timed-out (score recorded as 0).

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toBlob } from "html-to-image";
import wordmark from "../assets/Rosterable.png";
import { useAuth } from "../managers/AuthContext";
import { SPORT_CONFIG } from "../managers/draftManager";
import { loadPoolDay } from "../managers/knockoutService";
import { CircularScore } from "../components/DraftResult";
import { getChemistryBreakdown } from "../managers/teamRatingCalculator";

// App theme color (Rosterable lime). Used for the stat pill so it stands apart
// from the sport-colored score wheel / chemistry total.
const THEME = "#d4f53c";

// Score band (above/below the estimated cut) within which we call it a coin-flip
// rather than safely in/out. Tunable.
const BUBBLE_MARGIN = 10;

const SPORT_EMOJI = {
  football: "🏈",
  baseball: "⚾",
  basketball: "🏀",
  hockey: "🏒",
  soccer: "⚽",
};

function cutStatus(teamRating, estimatedCutScore, sportHex) {
  if (estimatedCutScore == null || estimatedCutScore <= 0) return null;
  const diff = teamRating - estimatedCutScore;
  if (diff >= BUBBLE_MARGIN) return { label: "CURRENTLY ADVANCING", color: sportHex };
  if (diff > -BUBBLE_MARGIN) return { label: "ON THE BUBBLE", color: "#f5a623" };
  return { label: "BELOW THE CUT", color: "#ff5a5a" };
}

// Theme-colored pill showing the live estimated cut and/or today's entry count.
function StatPill({ estimatedCutScore, entries }) {
  const items = [];
  if (estimatedCutScore != null) items.push({ value: estimatedCutScore, label: "EST. CUT" });
  if (entries > 0) items.push({ value: entries, label: "ENTRIES" });
  if (items.length === 0) return null;

  return (
    <div style={styles.statPill}>
      {items.map((it, i) => (
        <React.Fragment key={it.label}>
          {i > 0 && <div style={styles.statSep} />}
          <div style={styles.statItem}>
            <span style={styles.statValue}>{it.value}</span>
            <span style={styles.statLabel}>{it.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function KnockoutComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const state = location.state || {};
  const { sportId, poolId, dayNumber, poolTitle, timedOut } = state;

  // Direct navigation / refresh wipes state → return to the overview.
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    } else if (!sportId) {
      navigate("/battles/knockout", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [hasAppeared, setHasAppeared] = useState(false);
  const [day, setDay] = useState(null);
  const [shareLabel, setShareLabel] = useState("SHARE");

  const captureRef = useRef(null);
  const shareFileRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setHasAppeared(true), 120);
    return () => clearTimeout(t);
  }, []);

  // Load the live pool-day estimate (cut score + entries submitted today).
  useEffect(() => {
    let cancelled = false;
    if (!poolId || !dayNumber) return;
    loadPoolDay(poolId, dayNumber)
      .then((d) => { if (!cancelled) setDay(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [poolId, dayNumber]);

  const config = sportId ? SPORT_CONFIG[sportId] : null;

  // Pre-generate the share PNG from the capture block. Re-runs when `day`
  // resolves so the cut pill makes it into the image. Skipped on the timeout
  // screen (nothing worth sharing).
  useEffect(() => {
    if (timedOut || !sportId) return;
    let cancelled = false;
    async function generate() {
      try {
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
        // Let the entrance animations settle so nothing is captured mid-fade.
        await new Promise((r) => setTimeout(r, 1200));
        const node = captureRef.current;
        if (cancelled || !node) return;
        // Wait for images (wordmark) to load AND decode — mobile Safari embeds
        // them blank otherwise.
        const imgs = Array.from(node.querySelectorAll("img"));
        await Promise.all(imgs.map(async (img) => {
          if (!(img.complete && img.naturalWidth > 0)) {
            await new Promise((res) => {
              img.addEventListener("load", res, { once: true });
              img.addEventListener("error", res, { once: true });
            });
          }
          if (img.decode) { try { await img.decode(); } catch {} }
        }));
        if (cancelled || !node) return;
        let blob = null;
        const opts = { pixelRatio: 2, backgroundColor: "#0e1117" };
        // A few passes warm up html-to-image's data-URL embedding on Safari.
        for (let pass = 0; pass < 3; pass++) {
          blob = await toBlob(node, opts);
          if (cancelled) return;
        }
        if (cancelled || !blob) return;
        shareFileRef.current = new File([blob], `rosterable-knockout-${sportId}.png`, { type: "image/png" });
      } catch (err) {
        console.warn("Knockout share image generation failed:", err);
      }
    }
    generate();
    return () => { cancelled = true; };
  }, [sportId, timedOut, day]);

  if (!user || !sportId) return null;

  const sport = { id: sportId, hex: config.color };
  const goBack = () => navigate("/battles/knockout");
  const title = (poolTitle || "KNOCKOUT").toUpperCase();
  const entries = day?.usersSubmitted ?? 0;

  const players = state.players || [];
  const teamRating = state.teamRating ?? 0;
  const chemistry = getChemistryBreakdown(players);
  const hasChemistry = chemistry.totalChemistryPoints > 0;
  const status = cutStatus(teamRating, day?.estimatedCutScore, sport.hex);

  async function handleShare() {
    const file = shareFileRef.current;

    // 1) Native share with the image (mobile share sheet).
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Rosterable" });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }

    // 2) Download the PNG (desktop / no file-share support).
    if (file) {
      try {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setShareLabel("SAVED!");
        setTimeout(() => setShareLabel("SHARE"), 1500);
        return;
      } catch {
        // fall through to text
      }
    }

    // 3) Text fallback.
    const emoji = SPORT_EMOJI[sportId] || "🏆";
    const text =
      `ROSTERABLE\n` +
      `${emoji} ${title} ${emoji}\n` +
      `Score: ${teamRating}\n` +
      `Chemistry: ${hasChemistry ? `+${chemistry.totalChemistryPoints}` : "+0"}\n` +
      (status ? `${status.label}\n` : "") +
      `\nPlay daily at Rosterableapp.com`;

    if (navigator.share && (!navigator.canShare || navigator.canShare({ text }))) {
      try {
        await navigator.share({ title: "Rosterable", text });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        return;
      }
    }
    setShareLabel("COPIED!");
    setTimeout(() => setShareLabel("SHARE"), 1500);
  }

  const ambientGlow = (
    <div style={{
      position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
      background: `radial-gradient(ellipse at top, ${sport.hex}14, transparent 60%)`,
    }} />
  );

  const topBar = (
    <div style={styles.topBar}>
      <button style={styles.backBtn} onClick={goBack}>‹</button>
    </div>
  );

  const titleRow = (
    <div style={styles.titleRow}>
      <span style={{ ...styles.titleDot, background: sport.hex }} />
      <span style={styles.title}>{title}</span>
      <span style={{ ...styles.titleDot, background: sport.hex }} />
    </div>
  );

  // ── Timed-out state (no share) ───────────────────────────────────────────────
  if (timedOut) {
    return (
      <div style={styles.page}>
        {ambientGlow}
        <div style={styles.scroll}>
          {topBar}
          <img src={wordmark} alt="Rosterable" style={styles.brandWordmark} />
          {titleRow}
          <div style={styles.timeoutBox}>
            <div style={styles.timeoutIcon}>⏱</div>
            <span style={styles.timeoutTitle}>DRAFT TIMED OUT</span>
            <span style={styles.timeoutSubtitle}>Your score was recorded as 0.</span>
            {entries > 0 && <StatPill entries={entries} />}
          </div>
          <div style={styles.actionsSection}>
            <button style={{ ...styles.primaryBtn, background: sport.hex }} onClick={goBack}>
              BACK TO KNOCKOUT
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Completed state ──────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {ambientGlow}
      <div style={styles.scroll}>
        {topBar}

        {/* Everything inside captureRef becomes the share image. */}
        <div ref={captureRef} style={styles.captureBlock}>
          <img src={wordmark} alt="Rosterable" style={styles.brandWordmark} />
          {titleRow}

          {/* Large centered score wheel */}
          <div style={styles.wheelWrap}>
            <CircularScore
              teamRating={teamRating}
              sport={sport}
              hasAppeared={hasAppeared}
              size={188}
              strokeWidth={14}
              trackStrokeWidth={18}
              ratingFontSize="52px"
              scoreLabelFontSize="12px"
            />
          </div>

          {/* Chemistry total only — specific team/city bonuses are hidden so the
              share screen never reveals which clubs the roster used. */}
          <div style={{
            ...styles.chemCard,
            opacity: hasAppeared ? 1 : 0,
            transform: hasAppeared ? "translateY(0)" : "translateY(12px)",
            transition: "all 0.4s ease 0.2s",
          }}>
            <span style={{ ...styles.chemTotalNum, color: sport.hex }}>
              {hasChemistry ? `+${chemistry.totalChemistryPoints}` : "+0"}
            </span>
            <span style={styles.chemTotalLabel}>TEAM CHEMISTRY</span>
          </div>

          {/* Live cut-line status + theme-colored stat pill */}
          {(status || entries > 0) && (
            <div style={{
              ...styles.statusWrap,
              opacity: hasAppeared ? 1 : 0,
              transition: "opacity 0.4s ease 0.35s",
            }}>
              {status && (
                <div style={{ ...styles.statusBadge, color: status.color, borderColor: `${status.color}66`, background: `${status.color}14` }}>
                  {status.label}
                </div>
              )}
              <StatPill estimatedCutScore={day?.estimatedCutScore} entries={entries} />
              <span style={styles.statusNote}>Updates until tonight's cut</span>
            </div>
          )}

          <span style={styles.shareFooter}>rosterableapp.com</span>
        </div>

        {/* Actions (outside the capture) */}
        <div style={styles.actionsSection}>
          <button
            style={{ ...styles.shareBtn, borderColor: `${sport.hex}80` }}
            onClick={handleShare}
          >
            {shareLabel}
          </button>
          <button style={{ ...styles.primaryBtn, background: sport.hex }} onClick={goBack}>
            BACK TO KNOCKOUT
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", position: "relative" },
  scroll: {
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    position: "relative",
    zIndex: 1,
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    paddingTop: "34px",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: "28px",
    fontWeight: "300",
    lineHeight: 1,
    width: 44,
    textAlign: "left",
    padding: 0,
    cursor: "pointer",
  },
  captureBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingTop: "10px",
    paddingBottom: "6px",
  },
  brandWordmark: { height: "22px", width: "auto", alignSelf: "center" },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  titleDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  title: {
    fontSize: "17px",
    fontWeight: "800",
    color: "#fff",
    letterSpacing: "2.5px",
    textAlign: "center",
  },
  wheelWrap: { display: "flex", justifyContent: "center", padding: "10px 0 6px" },
  chemCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "18px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
  chemTotalNum: { fontSize: "40px", fontWeight: "800", lineHeight: 1 },
  chemTotalLabel: { fontSize: "12px", fontWeight: "800", color: "rgba(255,255,255,0.55)", letterSpacing: "2px" },
  statusWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", paddingTop: "2px" },
  statusBadge: {
    fontSize: "15px",
    fontWeight: "800",
    letterSpacing: "1.5px",
    padding: "8px 18px",
    borderRadius: "999px",
    border: "1px solid",
  },
  statPill: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
    padding: "10px 22px",
    borderRadius: "999px",
    background: `${THEME}1f`,
    border: `1px solid ${THEME}66`,
  },
  statItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "1px" },
  statValue: { fontSize: "20px", fontWeight: "800", color: THEME, lineHeight: 1 },
  statLabel: { fontSize: "9px", fontWeight: "700", color: "rgba(255,255,255,0.55)", letterSpacing: "1.5px" },
  statSep: { width: "1px", height: "26px", background: "rgba(255,255,255,0.15)" },
  statusNote: { fontSize: "11px", fontWeight: "500", color: "rgba(255,255,255,0.3)" },
  shareFooter: {
    fontSize: "11px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.35)",
    letterSpacing: "1px",
    textAlign: "center",
    paddingTop: "4px",
  },
  actionsSection: { display: "flex", flexDirection: "column", gap: "8px", paddingTop: "8px" },
  shareBtn: {
    height: "52px",
    borderRadius: "12px",
    border: "1px solid",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "800",
    letterSpacing: "1px",
    cursor: "pointer",
    width: "100%",
  },
  primaryBtn: {
    height: "52px",
    borderRadius: "12px",
    border: "none",
    color: "#000",
    fontSize: "16px",
    fontWeight: "800",
    letterSpacing: "0.5px",
    cursor: "pointer",
    width: "100%",
  },
  timeoutBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "40px 20px",
    textAlign: "center",
  },
  timeoutIcon: { fontSize: "56px", lineHeight: 1, color: "rgba(255,90,90,0.7)", marginBottom: "8px" },
  timeoutTitle: { fontSize: "20px", fontWeight: "800", color: "#fff", letterSpacing: "2px" },
  timeoutSubtitle: { fontSize: "14px", fontWeight: "500", color: "rgba(255,255,255,0.5)" },
};
